import graphqlRequest from "Services/assignment/assignmentRequest";
import { GET_SPANISH_CALIBRATION_ASSIGNMENT } from "./getAssignmentGraphql";
import { ENTITY_TYPE, ASSIGNMENT_STATUS, ASSIGNMENT_TYPE, GRADES } from "../constants";
import { addAssignment } from "../addAssignment/addAssignment";
import { getUserData } from "../../API";
import { logSessionInfo } from "../../logger";
import { transformAssignment } from "./getAssignment";

/**
 * Fetches an existing Spanish calibration assignment for a student
 * @param {string} studentId - Student ID
 * @returns {Promise<object|null>} - Assignment object or null if not found
 */
async function fetchSpanishCalibrationAssignment(studentId) {
  try {
    const response = await graphqlRequest(GET_SPANISH_CALIBRATION_ASSIGNMENT, {
      entityId: studentId,
      entityType: ENTITY_TYPE.STUDENT,
      filter: {
        status: [ASSIGNMENT_STATUS.ASSIGNED, ASSIGNMENT_STATUS.IN_PROGRESS],
        assignmentType: [ASSIGNMENT_TYPE.CALIBRATION],
        entityType: [ENTITY_TYPE.STUDENT]
      }
    });

    const assignments = response.assignment || [];
    
    // Filter to ensure we only get CALIBRATION assignments with correct entity type
    const calibrationAssignments = assignments.filter(assignment => 
      assignment.assignmentType === ASSIGNMENT_TYPE.CALIBRATION &&
      assignment.entityType === ENTITY_TYPE.STUDENT &&
      (assignment.status === ASSIGNMENT_STATUS.ASSIGNED || assignment.status === ASSIGNMENT_STATUS.IN_PROGRESS)
    );

    if (calibrationAssignments.length > 0) {
      const assignment = calibrationAssignments[0];
      logSessionInfo("spanish_calibration_assignment_found", { assignmentId: assignment.assignmentId });
      return transformAssignment(assignment);
    }

    logSessionInfo("spanish_calibration_assignment_not_found", { studentId });
    return null;
  } catch (error) {
    console.error("Error fetching Spanish calibration assignment:", error);
    logSessionInfo("spanish_calibration_assignment_fetch_error", { error: error.message, studentId });
    throw error;
  }
}

/**
 * Creates a new Spanish calibration assignment for a student
 * @param {string} studentId - Student ID
 * @returns {Promise<object>} - Created assignment object
 */
async function createSpanishCalibrationAssignment(studentId, schoolYear, periodId) {

  // Get current date for dateFrom
  const dateFrom = new Date().toISOString().split('T')[0]; // Format: "2025-11-10"
  const dateTo = "2026-05-31";

  // Get student grade and convert to enum (using same pattern as getAssignment.js line 250)
  const { actualGrade, sisGrade } = getUserData();
  const numericGrade = sisGrade || actualGrade;
  const grade = numericGrade === -1 ? GRADES[GRADES.length - 1] : GRADES[numericGrade];

  // Build Spanish calibration manifest
  const manifest = [
    { activityType: "beginningLetterSound", locale: "ES_MX", storyId: "UNK" },
    { activityType: "letterSound", locale: "ES_MX", storyId: "UNK" },
    { activityType: "assessment", locale: "ES_MX", storyId: "UNK" },
    { activityType: "readingComprehension", locale: "ES_MX", storyId: "UNK" },
    { activityType: "ran", locale: "ES_MX", storyId: "UNK" }
  ];

  // Build assignment input
  const assignmentInput = {
    assignmentType: ASSIGNMENT_TYPE.CALIBRATION,
    dateFrom: dateFrom,
    dateTo: dateTo,
    entityType: ENTITY_TYPE.STUDENT,
    assignor: "spanishCalibration",
    entityId: studentId,
    grade: grade,
    periodId: periodId,
    schoolYear: schoolYear,
    tags: ["CALIBRATION"],
    manifest: manifest
  };

  try {
    logSessionInfo("spanish_calibration_assignment_creating", { studentId, grade, periodId, schoolYear });
    const newAssignment = await addAssignment(assignmentInput);
    const transformedAssignment = transformAssignment(newAssignment);
    logSessionInfo("spanish_calibration_assignment_created", { assignmentId: newAssignment.assignmentId });
    return transformedAssignment;
  } catch (error) {
    console.error("Error creating Spanish calibration assignment:", error);
    logSessionInfo("spanish_calibration_assignment_create_error", { error: error.message, studentId });
    throw error;
  }
}

/**
 * Creates a new Spanish calibration assignment with retry logic
 * @param {string} studentId - Student ID
 * @param {number} retryCount - Current retry count (internal use)
 * @returns {Promise<object>} - Created assignment object
 */
async function createSpanishCalibrationAssignmentWithRetry(studentId, schoolYear, period, retryCount = 0) {
  try {
    return await createSpanishCalibrationAssignment(studentId, schoolYear, period);
  } catch (error) {
    if (retryCount < 1) {
      // Retry once with backoff
      const backoffDelay = 1000; // 1 second
      logSessionInfo("spanish_calibration_assignment_retry", {
        retryCount: retryCount + 1,
        studentId,
        backoffDelay
      });
      
      await new Promise(resolve => setTimeout(resolve, backoffDelay));
      return await createSpanishCalibrationAssignmentWithRetry(studentId, schoolYear, period, retryCount + 1);
    } else {
      // Max retries reached, throw error
      throw error;
    }
  }
}

/**
 * Gets or creates a Spanish calibration assignment for a student
 * @param {string} studentId - Student ID
 * @param {string} calibrationGuid - Spanish calibration GUID (for logging purposes)
 * @returns {Promise<object>} - Assignment object
 */
export async function getSpanishCalibrationAssignment(studentId, calibrationGuid, schoolYear, period) {
  logSessionInfo("spanish_calibration_assignment_flow_start", { studentId, calibrationGuid });

  // First, try to fetch existing assignment
  try {
    const existingAssignment = await fetchSpanishCalibrationAssignment(studentId);
    if (existingAssignment) {
      return existingAssignment;
    }
  } catch (error) {
    // If fetch fails, log but continue to creation attempt
    console.error("Failed to fetch Spanish calibration assignment, will attempt to create:", error);
    logSessionInfo("spanish_calibration_assignment_fetch_failed_attempting_create", { error: error.message });
  }

  // No existing assignment found, create a new one with retry
  try {
    return await createSpanishCalibrationAssignmentWithRetry(studentId, schoolYear, period);
  } catch (error) {
    // Creation failed after retries, throw error to be handled by caller
    console.error("Failed to create Spanish calibration assignment after retries:", error);
    throw error;
  }
}

